import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchWithCORSProxy, useWidgetTokenStore } from '@elfsight/lib-utils';
import type { DataValue, MediaValue } from '../sources';

type TwitterDataValue<T> = DataValue<T> & {
  media?: MediaValue<T> & { isPreviewLink?: boolean };
};

type UseUpdatedTwitterData<M> = {
  updatedData: DataValue<M>[];
  isLoadingTwitterData: boolean;
};

function shouldUpdateData<M>(
  cachedData: TwitterDataValue<M>[],
  newData: TwitterDataValue<M>[]
): boolean {
  if (cachedData.length !== newData.length) {
    return true;
  }

  const cachedIds = new Set(cachedData.map((item) => item.id));
  const newIds = new Set(newData.map((item) => item.id));

  if (cachedIds.size !== newIds.size) {
    return true;
  }

  return Array.from(newIds).some((id) => !cachedIds.has(id));
}

export function useUpdatedTwitterData<M>(
  data: TwitterDataValue<M>[]
): UseUpdatedTwitterData<M> {
  const widgetTokenStore = useWidgetTokenStore();
  const [updatedData, setUpdatedData] = useState<DataValue<M>[]>(data);
  const loadedTwitterMedia = useRef<Map<string, string>>(new Map());
  const cachedData = useRef<DataValue<M>[]>(data);

  useEffect(() => {
    const fetchTwitterMediaDetails = async () => {
      const requests = data.map(async (item) => {
        const { isPreviewLink, src, ...otherMediaSettings } = item.media || {};
        const mediaSrc = src as string;

        if (item.source.type !== 'twitter' || !isPreviewLink || !mediaSrc) {
          return item;
        }

        const cachedMediaSrc = loadedTwitterMedia.current.get(mediaSrc);
        if (cachedMediaSrc) {
          return {
            ...item,
            media: { ...otherMediaSettings, src: cachedMediaSrc }
          };
        }

        try {
          const response = await fetchWithCORSProxy(
            mediaSrc,
            widgetTokenStore.get()
          );

          if (!response.ok) {
            console.error('Failed to fetch media data');

            return { ...item, media: { ...otherMediaSettings, src: '' } };
          }
          const jsonData: TweetResult = await response.json();
          const currentMediaUrl = jsonData.mediaDetails?.[0]?.media_url_https;

          if (currentMediaUrl) {
            loadedTwitterMedia.current.set(mediaSrc, currentMediaUrl);

            return {
              ...item,
              media: { ...otherMediaSettings, src: currentMediaUrl }
            };
          }
        } catch (error) {
          console.error('Error fetching Twitter media:', error);
        }

        return { ...item, media: { ...otherMediaSettings, src: '' } };
      });

      const results = await Promise.all(requests);
      setUpdatedData(results as DataValue<M>[]);
    };

    if (shouldUpdateData(cachedData.current, data)) {
      cachedData.current = data;
      fetchTwitterMediaDetails();
    }
  }, [data]);

  const isLoading = useMemo(
    () => data.length !== updatedData.length,
    [data, updatedData]
  );

  return { updatedData, isLoadingTwitterData: isLoading };
}

interface TweetResult {
  __typename: string;
  lang: string;
  favorite_count: number;
  possibly_sensitive: boolean;
  created_at: string;
  display_text_range: number[];
  entities: TweetResultEntries;
  id_str: string;
  text: string;
  user: TweetResultUser;
  edit_control: TweetResultEditControl;
  mediaDetails: TweetResultMediaDetail[];
  photos: TweetResultPhoto[];
  conversation_count: number;
  news_action_type: string;
  isEdited: boolean;
  isStaleEdit: boolean;
}

interface TweetResultEntries {
  hashtags: never[];
  urls: TweetResultURL[];
  user_mentions: TweetResultUserMention[];
  symbols: never[];
  media: TweetResultMedia[];
}

interface TweetResultURL {
  display_url: string;
  expanded_url: string;
  indices: number[];
  url: string;
}

interface TweetResultUserMention {
  id_str: string;
  indices: number[];
  name: string;
  screen_name: string;
}

interface TweetResultMedia {
  display_url: string;
  expanded_url: string;
  indices: number[];
  url: string;
}

interface TweetResultUser {
  id_str: string;
  name: string;
  profile_image_url_https: string;
  screen_name: string;
  verified: boolean;
  is_blue_verified: boolean;
  profile_image_shape: string;
}

interface TweetResultEditControl {
  edit_tweet_ids: string[];
  editable_until_msecs: string;
  is_edit_eligible: boolean;
  edits_remaining: string;
}

interface TweetResultMediaDetail {
  display_url: string;
  expanded_url: string;
  ext_media_availability: {
    status: string;
  };
  indices: number[];
  media_url_https: string;
  original_info: {
    height: number;
    width: number;
    focus_rects: TweetResultMediaRect[];
  };
  sizes: TweetResultMediaSizes;
  type: string;
  url: string;
}

interface TweetResultMediaSizes {
  large: TweetResultMediaSize;
  medium: TweetResultMediaSize;
  small: TweetResultMediaSize;
  thumb: TweetResultMediaSize;
}

interface TweetResultMediaSize {
  h: number;
  resize: string;
  w: number;
}

interface TweetResultPhoto {
  backgroundColor: TweetResultBackgroundColor;
  cropCandidates: TweetResultMediaRect[];
  expandedUrl: string;
  url: string;
  width: number;
  height: number;
}

interface TweetResultBackgroundColor {
  red: number;
  green: number;
  blue: number;
}

interface TweetResultMediaRect {
  x: number;
  y: number;
  w: number;
  h: number;
}
